import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { fitDescription, fitTitle } from '../../shared/seo-fit.mjs';

const ORIGIN = 'https://tixinfo.com';

const setTag = (selector: string, create: () => HTMLElement, attr: string, value: string) => {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
  return el;
};

/**
 * Per-page head management: title, description, canonical, robots.
 * Search/404 pages pass noindex so crawlers don't waste budget on infinite
 * query spaces or soft-404s.
 */
const Meta = ({
  title,
  description,
  noindex = false,
}: {
  title: string;
  description?: string;
  noindex?: boolean;
}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    // event pages keep the head the edge wrote (price, fee basis, resale disclaimer)
    const own = pathname.includes('/event/');
    document.title = own ? title : fitTitle(title);

    if (description) {
      setTag('meta[name="description"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'description');
        return m;
      }, 'content', own ? description : fitDescription(description));
    }

    // Canonical: path only, no query strings.
    setTag('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }, 'href', ORIGIN + pathname);

    const robots = setTag('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      return m;
    }, 'content', noindex ? 'noindex, follow' : 'index, follow');
    return () => {
      // Reset so a noindex page never leaks its directive to the next route.
      robots.setAttribute('content', 'index, follow');
    };
  }, [title, description, pathname, noindex]);

  return null;
};

export default Meta;
